import {useCallback, useContext, useEffect, useRef, useState} from "react";
import {getStorage, ref, uploadBytesResumable, UploadTask} from "firebase/storage";
import {v4 as uuidv4} from "uuid";
import {firebaseApp} from "../../firebaseClient";
import {UploadItem, UploadResult, UploadStatus} from "./types";
import {AppContext, AppContextI} from "../context/ContextWrapper";
import {getAreaSoundFilePath, getTransitionUploadPaths} from "../upload/uploadPaths";
import {isPcmWav, isSupportedAudioFile, isWavFile, SUPPORTED_AUDIO_DESCRIPTION} from "./supportedAudio";

type QueueEntry = {
    file: File;
    onCompleted?: (result: UploadResult) => Promise<void> | void;
    onFailed?: (reason: "cancelled" | "error", message?: string) => void;
};

type Params = {
    maxConcurrent?: number;
};

const ACTIVE_STATUSES: UploadStatus[] = ["queued", "uploading"];
const isActiveStatus = (status: UploadStatus) => ACTIVE_STATUSES.includes(status);

const getAudioDurationMs = (file: File) => {
    return new Promise<number | undefined>((resolve) => {
        if (!file.type || !file.type.startsWith("audio/")) {
            resolve(undefined);
            return;
        }
        const audio = new Audio();
        const url = URL.createObjectURL(file);
        audio.preload = "metadata";
        const cleanup = () => {
            URL.revokeObjectURL(url);
            audio.src = "";
        };
        audio.addEventListener("loadedmetadata", () => {
            const duration = Number.isFinite(audio.duration) ? Math.round(audio.duration * 1000) : undefined;
            cleanup();
            resolve(duration);
        });
        audio.addEventListener("error", () => {
            cleanup();
            resolve(undefined);
        });
        audio.src = url;
    });
};

const getFileMetadata = async (file: File) => {
    return {
        mimeType: file.type || undefined,
        sizeBytes: file.size || undefined,
        durationMs: await getAudioDurationMs(file)
    };
};

export const useUploadQueue = ({maxConcurrent = 4}: Params = {}) => {
    const appContext: AppContextI = useContext<AppContextI>(AppContext);
    const [uploads, setUploads] = useState<UploadItem[]>([]);
    const [uploadPanelOpen, setUploadPanelOpen] = useState<boolean>(true);

    const uploadsRef = useRef<UploadItem[]>([]);
    const queuedIdsRef = useRef<string[]>([]);
    const entriesRef = useRef<Map<string, QueueEntry>>(new Map());
    const activeTasksRef = useRef<Map<string, UploadTask>>(new Map());
    const cancelledIdsRef = useRef<Set<string>>(new Set());
    const runningCountRef = useRef<number>(0);
    const isProcessingRef = useRef<boolean>(false);
    const processQueueRef = useRef<() => void>(() => null);
    const recentlyEnqueuedRef = useRef<Map<File, ReturnType<typeof setTimeout>>>(new Map());

    useEffect(() => {
        uploadsRef.current = uploads;
    }, [uploads]);

    const updateUpload = useCallback((id: string, patch: Partial<UploadItem>) => {
        setUploads((previous) => previous.map((item) => item.id === id ? {
            ...item,
            ...patch
        } : item));
    }, []);

    const releaseEntry = useCallback((id: string) => {
        activeTasksRef.current.delete(id);
        entriesRef.current.delete(id);
        cancelledIdsRef.current.delete(id);
    }, []);

    processQueueRef.current = useCallback(() => {
        if (isProcessingRef.current) {
            return;
        }
        isProcessingRef.current = true;
        try {
            while (runningCountRef.current < maxConcurrent && queuedIdsRef.current.length > 0) {
                const id = queuedIdsRef.current.shift();
                if (!id) {
                    continue;
                }
                if (cancelledIdsRef.current.has(id)) {
                    updateUpload(id, {status: "cancelled", error: undefined});
                    releaseEntry(id);
                    continue;
                }
                const entry = entriesRef.current.get(id);
                if (!entry) {
                    continue;
                }
                const file = entry.file;
                const originalFileName = file.name;
                const extensionIndex = originalFileName.lastIndexOf(".");
                const extension = extensionIndex >= 0 ? originalFileName.substring(extensionIndex) : "";
                const fileName = `${uuidv4()}${extension}`;
                const filePath = getAreaSoundFilePath(appContext.getArea()?.id);
                const uploadPaths = getTransitionUploadPaths(appContext.getArea()?.id);

                runningCountRef.current += 1;
                updateUpload(id, {status: "uploading", progress: 0, error: undefined});

                const storage = getStorage(firebaseApp);
                const primaryPath = uploadPaths[0] || filePath;
                const uploadTask = uploadBytesResumable(ref(storage, `${primaryPath}/${fileName}`), file);
                activeTasksRef.current.set(id, uploadTask);

                uploadTask.on(
                    "state_changed",
                    (snapshot) => {
                        if (cancelledIdsRef.current.has(id)) {
                            return;
                        }
                        const progress = snapshot.totalBytes > 0
                            ? Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
                            : 0;
                        updateUpload(id, {progress});
                    },
                    (error: any) => {
                        runningCountRef.current = Math.max(0, runningCountRef.current - 1);
                        const isCancelled = cancelledIdsRef.current.has(id) || error?.code === "storage/canceled";
                        const onFailedCb = entriesRef.current.get(id)?.onFailed;
                        if (isCancelled) {
                            updateUpload(id, {status: "cancelled", error: undefined});
                            try {
                                onFailedCb?.("cancelled");
                            } catch (cbError) {
                                console.warn("[upload] onFailed cancelled handler threw", cbError);
                            }
                        } else {
                            const message = error?.message || "Upload failed";
                            updateUpload(id, {status: "error", error: message});
                            try {
                                onFailedCb?.("error", message);
                            } catch (cbError) {
                                console.warn("[upload] onFailed error handler threw", cbError);
                            }
                        }
                        releaseEntry(id);
                        processQueueRef.current();
                    },
                    async () => {
                        try {
                            if (cancelledIdsRef.current.has(id)) {
                                updateUpload(id, {status: "cancelled", error: undefined});
                                const onFailedCb = entriesRef.current.get(id)?.onFailed;
                                try {
                                    onFailedCb?.("cancelled");
                                } catch (cbError) {
                                    console.warn("[upload] onFailed cancelled handler threw", cbError);
                                }
                            } else {
                                const secondaryPaths = uploadPaths.filter((path) => path !== primaryPath);
                                await Promise.all(secondaryPaths.map((path) => new Promise<void>((resolveSecondary, rejectSecondary) => {
                                    const secondaryTask = uploadBytesResumable(ref(storage, `${path}/${fileName}`), file);
                                    activeTasksRef.current.set(id, secondaryTask);
                                    secondaryTask.on(
                                        "state_changed",
                                        () => null,
                                        (secondaryError: any) => rejectSecondary(secondaryError),
                                        () => resolveSecondary()
                                    );
                                })));
                                const metadata = await getFileMetadata(file);
                                const onCompleted = entriesRef.current.get(id)?.onCompleted;
                                if (onCompleted) {
                                    await onCompleted({
                                        originalFileName,
                                        fileName,
                                        filePath: filePath,
                                        metadata
                                    });
                                }
                                updateUpload(id, {status: "done", progress: 100, error: undefined});
                            }
                        } catch (error: any) {
                            const message = error?.message || "Upload failed";
                            updateUpload(id, {status: "error", error: message});
                            const onFailedCb = entriesRef.current.get(id)?.onFailed;
                            try {
                                onFailedCb?.("error", message);
                            } catch (cbError) {
                                console.warn("[upload] onFailed error handler threw", cbError);
                            }
                        } finally {
                            runningCountRef.current = Math.max(0, runningCountRef.current - 1);
                            releaseEntry(id);
                            processQueueRef.current();
                        }
                    }
                );
            }
        } finally {
            isProcessingRef.current = false;
        }
    }, [appContext, maxConcurrent, releaseEntry, updateUpload]);

    const queueFiles = useCallback((files: File[], onCompleted?: (result: UploadResult) => Promise<void> | void, onFailed?: (reason: "cancelled" | "error", message?: string) => void) => {
        const freshFiles = files.filter((file) => !recentlyEnqueuedRef.current.has(file));
        if (!freshFiles.length) {
            return;
        }
        freshFiles.forEach((file) => {
            const timeoutId = setTimeout(() => {
                recentlyEnqueuedRef.current.delete(file);
            }, 1500);
            recentlyEnqueuedRef.current.set(file, timeoutId);
        });
        const nextItems: UploadItem[] = [];
        freshFiles.forEach((file) => {
            const id = uuidv4();
            entriesRef.current.set(id, {file, onCompleted, onFailed});
            cancelledIdsRef.current.delete(id);
            queuedIdsRef.current.push(id);
            nextItems.push({
                id,
                name: file.name,
                status: "queued",
                progress: 0
            });
        });
        setUploads((previous) => [...nextItems, ...previous]);
        setUploadPanelOpen(true);
        processQueueRef.current();
    }, []);

    const enqueueUploads = useCallback((files: File[], onCompleted?: (result: UploadResult) => Promise<void> | void, onFailed?: (reason: "cancelled" | "error", message?: string) => void) => {
        if (!files.length) {
            return;
        }
        const formatRejected: string[] = [];
        const supportedFiles: File[] = [];
        files.forEach((file) => {
            if (isSupportedAudioFile(file)) {
                supportedFiles.push(file);
            } else {
                formatRejected.push(file.name);
            }
        });
        if (formatRejected.length > 0) {
            const summary = formatRejected.length === 1
                ? `"${formatRejected[0]}" was not added`
                : `${formatRejected.length} files were not added`;
            appContext.showError(`${summary}: supported formats are ${SUPPORTED_AUDIO_DESCRIPTION}.`);
            try {
                onFailed?.("error", `Unsupported audio format. Supported formats are ${SUPPORTED_AUDIO_DESCRIPTION}.`);
            } catch (callbackError) {
                console.warn("[upload] onFailed unsupported handler threw", callbackError);
            }
        }
        if (!supportedFiles.length) {
            return;
        }
        const nonWavFiles = supportedFiles.filter((file) => !isWavFile(file));
        const wavFiles = supportedFiles.filter((file) => isWavFile(file));
        if (nonWavFiles.length > 0) {
            queueFiles(nonWavFiles, onCompleted, onFailed);
        }
        if (wavFiles.length === 0) {
            return;
        }
        Promise.all(wavFiles.map(async (file) => ({file, ok: await isPcmWav(file)})))
            .then((results) => {
                const pcmFiles = results.filter((entry) => entry.ok).map((entry) => entry.file);
                const nonPcmNames = results.filter((entry) => !entry.ok).map((entry) => entry.file.name);
                if (nonPcmNames.length > 0) {
                    const summary = nonPcmNames.length === 1
                        ? `"${nonPcmNames[0]}" was not added`
                        : `${nonPcmNames.length} files were not added`;
                    appContext.showError(`${summary}: WAV files must use PCM encoding.`);
                    try {
                        onFailed?.("error", "WAV files must use PCM encoding.");
                    } catch (callbackError) {
                        console.warn("[upload] onFailed non-PCM WAV handler threw", callbackError);
                    }
                }
                if (pcmFiles.length > 0) {
                    queueFiles(pcmFiles, onCompleted, onFailed);
                }
            })
            .catch((error) => {
                console.warn("[upload] WAV PCM validation failed", error);
            });
    }, [appContext, queueFiles]);

    const cancelUpload = useCallback((id: string) => {
        cancelledIdsRef.current.add(id);
        queuedIdsRef.current = queuedIdsRef.current.filter((queuedId) => queuedId !== id);
        const activeTask = activeTasksRef.current.get(id);
        if (activeTask) {
            activeTask.cancel();
        } else {
            updateUpload(id, {status: "cancelled", error: undefined});
            releaseEntry(id);
            processQueueRef.current();
        }
    }, [releaseEntry, updateUpload]);

    const cancelAllUploads = useCallback(() => {
        const activeIds = uploadsRef.current.filter((item) => isActiveStatus(item.status)).map((item) => item.id);
        activeIds.forEach((id) => cancelUpload(id));
    }, [cancelUpload]);

    const clearCompletedUploads = useCallback(() => {
        setUploads((previous) => previous.filter((item) => isActiveStatus(item.status)));
    }, []);

    return {
        uploads,
        uploadPanelOpen,
        setUploadPanelOpen,
        enqueueUploads,
        cancelUpload,
        cancelAllUploads,
        clearCompletedUploads
    };
};
